'use es6';

import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import clone from 'hs-lodash/clone';
import defer from 'hs-lodash/defer';
var DEFAULT_STATUS = {
  isActive: false,
  isEnrolled: false,
  isDefaulted: true,
  isCached: false
};
var CACHED_STATUS = {
  isActive: false,
  isEnrolled: true,
  isDefaulted: false,
  isCached: true
};
export function adaptParametersFromApi() {
  var parameters = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return parameters.reduce(function (acc, parameter) {
    acc[parameter.key] = parameter.value;
    return acc;
  }, {});
}
export function adaptTreatmentsFromApi() {
  var treatments = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return treatments.reduce(function (acc, treatment) {
    acc[treatment.key] = Object.assign({}, treatment, {
      parameters: adaptParametersFromApi(treatment.parameters || [])
    });
    return acc;
  }, {});
}
export function hasValidStatus() {
  var treatment = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return !!(treatment && treatment.status && typeof treatment.status.isActive === 'boolean' && typeof treatment.status.isEnrolled === 'boolean' && typeof treatment.status.isDefaulted === 'boolean');
}
export function canExpose() {
  var treatment = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return !!(treatment && treatment.key && treatment.identifier);
}
export function shouldExpose() {
  var treatment = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!canExpose(treatment)) return false;
  if (!treatment.status) return true;
  return !treatment.status.isDefaulted;
}
export function shouldUseOption() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var value = arguments.length > 1 ? arguments[1] : undefined;
  return value && options.indexOf(value) !== -1;
}
export function resolveParameter() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var values = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return values.find(function (_ref) {
    var value = _ref.value;
    return shouldUseOption(options, value);
  }) || values[values.length - 1];
}
export function resolveParameters(treatmentKey) {
  var parameters = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var schema = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var overridesFromCache = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var overridesFromSearch = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  var statusOverride;
  var resolvedParameters = Object.keys(schema).reduce(function (acc, paramKey) {
    var options = schema[paramKey];
    var cacheKey = treatmentKey + "-" + paramKey;
    var overrideFromSearch = overridesFromSearch[cacheKey];
    var overrideFromCache = overridesFromCache[cacheKey];
    var valueFromNetwork = parameters[paramKey];
    var values = [{
      value: overrideFromSearch,
      status: null
    }, {
      value: overrideFromCache,
      status: CACHED_STATUS
    }, {
      value: valueFromNetwork,
      status: null
    }, {
      value: options[0],
      status: DEFAULT_STATUS
    }];

    var _resolveParameter = resolveParameter(options, values),
        value = _resolveParameter.value,
        status = _resolveParameter.status;

    if (status) {
      statusOverride = status;
    }

    acc[paramKey] = value;
    return acc;
  }, parameters);
  return {
    resolvedParameters: resolvedParameters,
    statusOverride: statusOverride
  };
}
export function resolveTreatment(treatment) {
  var definition = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var overridesFromSearch = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var overridesFromCache = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var parameters = treatment ? treatment.parameters || {} : {};
  var status = hasValidStatus(treatment) ? treatment.status : DEFAULT_STATUS;
  var resolved = clone(treatment || definition);

  var _resolveParameters = resolveParameters(definition.key, parameters, definition.parameters, overridesFromCache, overridesFromSearch),
      resolvedParameters = _resolveParameters.resolvedParameters,
      statusOverride = _resolveParameters.statusOverride;

  resolved.parameters = resolvedParameters;
  resolved.status = statusOverride || status;

  if (typeof resolved.status.isCached === 'undefined') {
    resolved.status.isCached = false;
  }

  if (typeof definition.maxAge === 'number') {
    resolved.maxAge = definition.maxAge;
  }

  return resolved;
}
export function resolveTreatments() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$treatments = _ref2.treatments,
      treatments = _ref2$treatments === void 0 ? [] : _ref2$treatments,
      _ref2$schema = _ref2.schema,
      schema = _ref2$schema === void 0 ? {} : _ref2$schema,
      _ref2$overridesFromCa = _ref2.overridesFromCache,
      overridesFromCache = _ref2$overridesFromCa === void 0 ? {} : _ref2$overridesFromCa,
      _ref2$overridesFromSe = _ref2.overridesFromSearch,
      overridesFromSearch = _ref2$overridesFromSe === void 0 ? {} : _ref2$overridesFromSe;

  var adaptedTreatments = adaptTreatmentsFromApi(treatments);
  return Object.keys(schema).reduce(function (acc, key) {
    var definition = schema[key];
    definition.key = key;
    var treatment = adaptedTreatments[key];
    acc[key] = resolveTreatment(treatment, definition, overridesFromSearch, overridesFromCache);
    return acc;
  }, adaptedTreatments);
}
export function resolveExposure(treatmentOrKey) {
  var treatments = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var keyMode = typeof treatmentOrKey === 'string';
  var treatment = keyMode ? treatments[treatmentOrKey] : treatmentOrKey;
  if (!shouldExpose(treatment)) return null;
  return {
    key: treatment.key,
    identifier: treatment.identifier
  };
}
export function resolveExposures() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref3$treatments = _ref3.treatments,
      treatments = _ref3$treatments === void 0 ? {} : _ref3$treatments,
      _ref3$treatmentOrKeys = _ref3.treatmentOrKeys,
      treatmentOrKeys = _ref3$treatmentOrKeys === void 0 ? [] : _ref3$treatmentOrKeys;

  return treatmentOrKeys.map(function (treatmentOrKey) {
    return resolveExposure(treatmentOrKey, treatments);
  }).filter(function (exposure) {
    return !!exposure;
  });
}
export var createDeferredCallstack = function createDeferredCallstack(fnHead) {
  var callstack = fnHead;

  var deferExecution = function deferExecution(data) {
    defer(function () {
      if (typeof callstack === 'function') {
        var ref = callstack;
        callstack = undefined;
        ref(data);
      }
    });
  };

  return {
    execute: deferExecution,
    defer: function defer(fnTail) {
      var ref = callstack;

      callstack = function callstack(data) {
        if (typeof ref === 'function') ref(data);
        fnTail(data);
      };
    }
  };
};
export function createAsyncResolver() {
  var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      getAsyncData = _ref4.getAsyncData,
      resolve = _ref4.resolve,
      _onError = _ref4.onError;

  var cache;
  var callstack;
  return function (callback) {
    if (callstack) {
      callstack.defer(callback);

      if (cache) {
        callstack.execute(resolve(cache));
      }
    } else {
      callstack = createDeferredCallstack(callback);
      getAsyncData({
        onComplete: function onComplete(data) {
          cache = data || {};
          callstack.execute(resolve(cache));
        },
        onError: function onError(err, data) {
          if (typeof _onError === 'function') {
            _onError(err);
          }

          cache = data;
          callstack.execute(resolve(cache));
        }
      });
    }
  };
}
export function deserializeCopyVariant(serialized) {
  var parts = serialized.split(':::');
  var key = parts[1];
  var variant = parts[2].split('__')[0];
  return [key, variant];
}
export function isCopyVariant(key) {
  return key.indexOf('__copym:::') !== -1;
}
export function isSwappableCopyVariant(key, treatments) {
  var _deserializeCopyVaria = deserializeCopyVariant(key),
      _deserializeCopyVaria2 = _slicedToArray(_deserializeCopyVaria, 2),
      treatmentKey = _deserializeCopyVaria2[0],
      variant = _deserializeCopyVaria2[1];

  var treatment = treatments[treatmentKey];
  return treatment && treatment.parameters && treatment.parameters['group'] === variant;
}
export function findSwappableCopy() {
  var _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref5$translations = _ref5.translations,
      translations = _ref5$translations === void 0 ? {} : _ref5$translations,
      _ref5$treatments = _ref5.treatments,
      treatments = _ref5$treatments === void 0 ? {} : _ref5$treatments,
      onError = _ref5.onError;

  try {
    var flatTranslations = [translations, translations['translate-copy'], translations['do-not-translate-copy']].filter(function (branch) {
      return !!branch;
    }).reduce(function (acc, branch) {
      return Object.assign({}, acc, {}, branch);
    }, {});
    var copyVariants = Object.keys(flatTranslations).filter(isCopyVariant);
    return copyVariants.reduce(function (acc, key) {
      if (isSwappableCopyVariant(key, treatments)) {
        acc[key] = flatTranslations[key];
      }

      return acc;
    }, {});
  } catch (err) {
    defer(function () {
      if (typeof onError === 'function') {
        onError(err);
      }
    });
    return {};
  }
}